// The rules-engine evaluator — one pure function shared by every configurator
// surface (see RULES-ENGINE-DESIGN.md): reactive recomputes, interrogative
// candidate checks, enumeration, and server-side revalidation all call this.
// evaluate(state, rules, catalog) reads canonical state plus a rule set and
// produces a derived overlay (forced/locked/restricted picks, forced
// variants, auto-attached slot contents, option restrictions, validation
// flags) plus a rule trace and structured authoring errors. Canonical state
// is never mutated — change the trigger, recompute, and the force/flag/
// grey-out evaporates. No Date.now, no randomness: same inputs, same output.
//
// Evaluation is a fixed point with a hard pass cap (MAX_PASSES): each pass
// re-evaluates every rule against the *effective* state (canonical merged
// with the previous pass's overlay), so chaining works (A forces B, a rule on
// B fires next pass). Non-convergence is a structured authoring error — a
// rule cycle — never a runtime loop. Conflicts resolve first-wins by author
// order per target (surface/hook/variant) and the loser is recorded in the
// trace with `suppressedBy`; flag validations union and never conflict.
//
// seedMaterial is the one transition-shaped effect, and the evaluator stays
// stateless about it: it *reports* pending seeds plus per-binding condition
// outcomes (EvalResult.conditionResults / pendingSeeds). The caller keeps the
// previous conditionResults and writes a seed into canonical state exactly
// when computeSeedTransitions() shows its condition newly became true.

import type {
  ConditionResults,
  Condition,
  ConfigInstance,
  ConfigurationState,
  EvalError,
  EvalResult,
  FactValue,
  Overlay,
  PendingSeed,
  Rule,
  RuleCatalog,
  RuleTrace,
  TraceEntry,
  TracePass,
  Effect,
  FlagTarget,
} from "./types";
import { RESERVED_OPS } from "./types";
import {
  buildView,
  resolveFact,
  resolveRelation,
  RuleConditionError,
  type EffectiveView,
} from "./facts";
import {
  emptyOverlay,
  getInstanceOverlay,
  mergeEffective,
  stableAutoAttachKey,
} from "./overlay";

/** Hard fixed-point pass cap. A rule set that hasn't converged by then holds
 *  a cycle — an authoring error, surfaced as a structured EvalError. */
export const MAX_PASSES = 5;

const RESERVED_OP_SET: ReadonlySet<string> = new Set(RESERVED_OPS);

/** Key for a rule×binding condition outcome: `${ruleId}@${instanceId}` for
 *  product-scoped bindings, `${ruleId}@$scene` for scene scope. */
function conditionKey(ruleId: string, binding: ConfigInstance | null): string {
  return `${ruleId}@${binding ? binding.instanceId : "$scene"}`;
}

/** Deterministic serialization (sorted keys) — used for the fixed-point
 *  overlay comparison so convergence never depends on insertion order. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const record = value as Record<string, unknown>;
  const parts = Object.keys(record)
    .sort()
    .filter((k) => record[k] !== undefined)
    .map((k) => `${JSON.stringify(k)}:${stableStringify(record[k])}`);
  return `{${parts.join(",")}}`;
}

/** Evaluate one condition against the effective view. Structured authoring
 *  problems (unknown fact/op/relation, instance-scoped fact in scene scope)
 *  raise RuleConditionError, which the caller records and survives. Reserved
 *  numeric ops (count/gt/gte/lt/lte) throw outright — they are grammar-only
 *  until the aggregate family is built. */
function evalCondition(
  view: EffectiveView,
  catalog: RuleCatalog,
  condition: Condition,
  instance: ConfigInstance | null,
  notes: string[],
): boolean {
  if ("all" in condition) {
    return condition.all.every((c) => evalCondition(view, catalog, c, instance, notes));
  }
  if ("any" in condition) {
    return condition.any.some((c) => evalCondition(view, catalog, c, instance, notes));
  }
  if ("not" in condition) {
    return !evalCondition(view, catalog, condition.not, instance, notes);
  }
  if ("exists" in condition) {
    const related = resolveRelation(view, condition.exists.in, instance, notes);
    return related.some((r) =>
      evalCondition(view, catalog, condition.exists.where, r, notes),
    );
  }
  if ("fact" in condition) {
    const op = (condition as { op?: string }).op ?? "";
    if (RESERVED_OP_SET.has(op)) {
      // Deliberate hard throw: reserved ops are an authoring error the rule
      // editor must reject; silently skipping would hide budget-rule intent.
      throw new Error(
        `Condition op "${op}" is reserved, not implemented (numeric/aggregate ops arrive in a later phase)`,
      );
    }
    const actual = resolveFact(view, catalog, condition.fact, instance);
    switch (op) {
      case "equals":
        return actual === (condition as { value: FactValue }).value;
      case "notEquals":
        return actual !== (condition as { value: FactValue }).value;
      case "in":
        return ((condition as { values?: FactValue[] }).values ?? []).includes(actual);
      default:
        throw new RuleConditionError(
          "unknownOp",
          `Unknown condition op "${op}" — ops are enumerated and namespaced; new ops are deliberate code changes`,
        );
    }
  }
  throw new RuleConditionError(
    "unknownOp",
    `Unrecognized condition shape: ${JSON.stringify(condition)}`,
  );
}

interface PassResult {
  overlay: Overlay;
  tracePass: TracePass;
  conditionResults: ConditionResults;
  pendingSeeds: PendingSeed[];
}

type PushError = (error: EvalError) => void;

/** Apply one fired effect into the pass overlay, recording the trace entry
 *  (including first-wins suppression) and any structured effect errors. */
function applyEffect(
  overlay: Overlay,
  rule: Rule,
  effect: Effect,
  binding: ConfigInstance | null,
  entry: TraceEntry,
  pushError: PushError,
  pendingSeeds: PendingSeed[],
  seedKeys: Set<string>,
  condKey: string,
): void {
  const kind = (effect as { effect?: string }).effect ?? "";
  // Effect-level message falls back to the rule's authored message.
  const message =
    (effect as { messageDvarId?: string }).messageDvarId ?? rule.message ?? null;
  // Server-resolved translated text for the rule-level message — carried next
  // to the id so UIs can render it without a client-side DVar resolver.
  const messageText = rule.messageText ?? null;

  // flag is the only effect that can land without an instance (scene target);
  // every constraint needs a target instance to write onto.
  if (!binding && kind !== "flag") {
    pushError({
      kind: "effectRequiresInstance",
      ruleId: rule.id,
      message: `Effect "${kind}" requires an instance target but rule "${rule.id}" is scene-scoped`,
    });
    entry.effects.push({ description: `${kind} skipped — requires instance scope` });
    return;
  }

  switch (kind) {
    case "forceMaterial": {
      const e = effect as Extract<Effect, { effect: "forceMaterial" }>;
      const io = getInstanceOverlay(overlay, binding!.instanceId);
      const description = `forceMaterial ${e.surfaceKey}=${e.materialId}`;
      const existing = io.forcedPicks[e.surfaceKey];
      if (existing) {
        entry.effects.push({ description, suppressedBy: existing.ruleId });
      } else {
        io.forcedPicks[e.surfaceKey] = { materialId: e.materialId, ruleId: rule.id };
        entry.effects.push({ description });
      }
      break;
    }
    case "seedMaterial": {
      // Transition seed — never an overlay entry. Reported for the caller to
      // apply to canonical state when the condition transitions to true.
      const e = effect as Extract<Effect, { effect: "seedMaterial" }>;
      const key = `${condKey}|${e.surfaceKey}`;
      if (!seedKeys.has(key)) {
        seedKeys.add(key);
        pendingSeeds.push({
          instanceId: binding!.instanceId,
          surfaceKey: e.surfaceKey,
          materialId: e.materialId,
          ruleId: rule.id,
          conditionKey: condKey,
        });
      }
      entry.effects.push({
        description: `seedMaterial ${e.surfaceKey}=${e.materialId} (pending — caller applies on condition transition)`,
      });
      break;
    }
    case "restrictMaterials": {
      const e = effect as Extract<Effect, { effect: "restrictMaterials" }>;
      const io = getInstanceOverlay(overlay, binding!.instanceId);
      const list = (io.restrictedMaterials[e.surfaceKey] ??= []);
      list.push({
        allowedMaterialIds: e.allowedMaterialIds,
        blockedMaterialIds: e.blockedMaterialIds,
        ruleId: rule.id,
        messageDvarId: message,
        messageText,
      });
      entry.effects.push({ description: `restrictMaterials ${e.surfaceKey}` });
      break;
    }
    case "lockSurface": {
      const e = effect as Extract<Effect, { effect: "lockSurface" }>;
      const io = getInstanceOverlay(overlay, binding!.instanceId);
      if (!io.lockedSurfaces[e.surfaceKey]) {
        io.lockedSurfaces[e.surfaceKey] = { ruleId: rule.id };
      }
      entry.effects.push({ description: `lockSurface ${e.surfaceKey}` });
      break;
    }
    case "forceVariant": {
      const e = effect as Extract<Effect, { effect: "forceVariant" }>;
      const io = getInstanceOverlay(overlay, binding!.instanceId);
      const description = `forceVariant ${e.variantKey}`;
      if (io.forcedVariant) {
        entry.effects.push({ description, suppressedBy: io.forcedVariant.ruleId });
      } else {
        io.forcedVariant = { variantKey: e.variantKey, ruleId: rule.id };
        entry.effects.push({ description });
      }
      break;
    }
    case "restrictOptions": {
      const e = effect as Extract<Effect, { effect: "restrictOptions" }>;
      const io = getInstanceOverlay(overlay, binding!.instanceId);
      const list = (io.restrictedOptions[e.hookKey] ??= []);
      list.push({
        allowedProductIds: e.allowedProductIds,
        blockedProductIds: e.blockedProductIds,
        ruleId: rule.id,
        messageDvarId: message,
        messageText,
      });
      entry.effects.push({ description: `restrictOptions ${e.hookKey}` });
      break;
    }
    case "autoAttach": {
      const e = effect as Extract<Effect, { effect: "autoAttach" }>;
      const io = getInstanceOverlay(overlay, binding!.instanceId);
      const description = `autoAttach ${e.hookKey}=${e.productId}`;
      const existing = io.forcedSlotContents[e.hookKey];
      if (existing) {
        entry.effects.push({ description, suppressedBy: existing.ruleId });
      } else {
        io.forcedSlotContents[e.hookKey] = {
          productId: e.productId,
          ruleId: rule.id,
          attachKey: stableAutoAttachKey(e.hookKey, rule.id),
        };
        entry.effects.push({ description });
      }
      break;
    }
    case "blockAttachment": {
      // Recorded only — the free-drag enforcement point (Unity DragValidation)
      // does not exist yet; the slot-UI equivalent is restrictOptions.
      const e = effect as Extract<Effect, { effect: "blockAttachment" }>;
      const io = getInstanceOverlay(overlay, binding!.instanceId);
      io.blockedAttachments.push({
        hookKey: e.hookKey ?? null,
        ruleId: rule.id,
        messageDvarId: message,
        messageText,
      });
      entry.effects.push({
        description: `blockAttachment${e.hookKey ? ` ${e.hookKey}` : ""} (recorded only — Unity DragValidation deferred)`,
      });
      break;
    }
    case "flag": {
      const e = effect as Extract<Effect, { effect: "flag" }>;
      const target: FlagTarget = e.target ?? (binding ? { type: "instance" } : { type: "scene" });
      const flagEntry = {
        ruleId: rule.id,
        severity: e.severity,
        messageDvarId: message,
        messageText,
        target,
      };
      if (!binding || target.type === "scene") {
        overlay.sceneFlags.push(flagEntry);
      } else {
        getInstanceOverlay(overlay, binding.instanceId).flags.push(flagEntry);
      }
      entry.effects.push({ description: `flag ${e.severity}` });
      break;
    }
    default:
      pushError({
        kind: "unknownEffect",
        ruleId: rule.id,
        instanceId: binding?.instanceId,
        message: `Unknown effect "${kind}" — effects are enumerated; new effects are deliberate code changes`,
      });
      entry.effects.push({ description: `${kind || "(missing effect)"} skipped — unknown effect` });
  }
}

/** Run one full pass: every rule (author order) against the effective view,
 *  building a fresh overlay. First-wins conflicts resolve within the pass. */
function runPass(
  view: EffectiveView,
  rules: Rule[],
  catalog: RuleCatalog,
  gates: Map<string, boolean>,
  passNumber: number,
  pushError: PushError,
): PassResult {
  const overlay = emptyOverlay();
  const tracePass: TracePass = { pass: passNumber, entries: [] };
  const conditionResults: ConditionResults = {};
  const pendingSeeds: PendingSeed[] = [];
  const seedKeys = new Set<string>();

  for (const rule of rules) {
    if (gates.get(rule.id) === false) {
      tracePass.entries.push({
        ruleId: rule.id,
        fired: false,
        gated: true,
        effects: [],
        notes: ["appliesWhen context gate did not match"],
      });
      continue;
    }
    // Product-scoped rules bind per instance (synthetic auto-attached
    // instances included, so their rules run too); scene rules bind once.
    const bindings: (ConfigInstance | null)[] =
      rule.scope === "scene" ? [null] : view.state.instances;

    for (const binding of bindings) {
      const notes: string[] = [];
      const condKey = conditionKey(rule.id, binding);
      let fired = false;
      let conditionFailed = false;
      try {
        fired = evalCondition(view, catalog, rule.when, binding, notes);
      } catch (err) {
        if (!(err instanceof RuleConditionError)) throw err; // reserved-op throw escapes by design
        conditionFailed = true;
        notes.push(err.message);
        pushError({
          kind: err.kind,
          ruleId: rule.id,
          instanceId: binding?.instanceId,
          message: err.message,
        });
      }
      if (!conditionFailed) conditionResults[condKey] = fired;

      const entry: TraceEntry = {
        ruleId: rule.id,
        instanceId: binding?.instanceId,
        fired,
        effects: [],
        notes,
      };
      if (fired) {
        for (const effect of rule.then) {
          applyEffect(overlay, rule, effect, binding, entry, pushError, pendingSeeds, seedKeys, condKey);
        }
      }
      // Keep the trace signal-dense: record firings, failures, and anything
      // with resolver notes; skip the silent non-matches.
      if (fired || conditionFailed || notes.length > 0) tracePass.entries.push(entry);
    }
  }
  return { overlay, tracePass, conditionResults, pendingSeeds };
}

/** Evaluate a rule set against a configuration. Pure: same (state, rules,
 *  catalog) → same EvalResult. Structured authoring errors (unknown
 *  fact/op/effect, non-convergence) are returned in `errors`; only reserved
 *  numeric ops throw. */
export function evaluate(
  state: ConfigurationState,
  rules: Rule[],
  catalog: RuleCatalog,
): EvalResult {
  const errors: EvalError[] = [];
  const errorKeys = new Set<string>();
  const pushError: PushError = (error) => {
    // Dedupe: the same authoring mistake would otherwise repeat per pass.
    const key = `${error.kind}|${error.ruleId ?? ""}|${error.instanceId ?? ""}|${error.message}`;
    if (errorKeys.has(key)) return;
    errorKeys.add(key);
    errors.push(error);
  };
  const trace: RuleTrace = { passes: [], converged: false, notes: [] };

  // appliesWhen gates are context-only: evaluate once, against canonical state
  // with no instance binding. A gate that errors is treated as closed.
  const gates = new Map<string, boolean>();
  const baseView = buildView(state);
  for (const rule of rules) {
    if (rule.appliesWhen == null) {
      gates.set(rule.id, true);
      continue;
    }
    let open = false;
    try {
      open = evalCondition(baseView, catalog, rule.appliesWhen, null, trace.notes);
    } catch (err) {
      if (!(err instanceof RuleConditionError)) throw err;
      pushError({ kind: err.kind, ruleId: rule.id, message: `appliesWhen: ${err.message}` });
    }
    gates.set(rule.id, open);
  }

  let previous = emptyOverlay();
  let conditionResults: ConditionResults = {};
  let pendingSeeds: PendingSeed[] = [];
  let converged = false;

  for (let pass = 1; pass <= MAX_PASSES; pass++) {
    // Each pass sees the effective state under the previous pass's overlay —
    // this is what makes chaining (and auto-attach cascades) work.
    const view = buildView(mergeEffective(state, previous));
    const result = runPass(view, rules, catalog, gates, pass, pushError);
    trace.passes.push(result.tracePass);
    conditionResults = result.conditionResults;
    pendingSeeds = result.pendingSeeds;
    if (stableStringify(result.overlay) === stableStringify(previous)) {
      converged = true;
      break;
    }
    previous = result.overlay;
  }

  if (!converged) {
    const message = `Rule set did not converge within MAX_PASSES=${MAX_PASSES} — a rule cycle (authoring error); see the trace passes for the oscillating rules`;
    pushError({ kind: "nonConvergence", message });
    trace.notes.push(message);
  }
  trace.converged = converged;

  return { overlay: previous, trace, errors, conditionResults, pendingSeeds };
}

/** Edge detection for transition seeds: the condition keys that are true in
 *  `next` but were not true in `prev`. The caller keeps the previous
 *  evaluation's conditionResults, calls this after every evaluate(), and
 *  applies exactly the pendingSeeds whose conditionKey is returned — that is
 *  the entire "apply once, when the condition becomes true" contract, and it
 *  keeps the evaluator itself stateless. */
export function computeSeedTransitions(
  prev: ConditionResults | null | undefined,
  next: ConditionResults,
): string[] {
  return Object.keys(next)
    .filter((key) => next[key] === true && prev?.[key] !== true)
    .sort();
}
